import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext
} from 'react';

import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import AppSnackbar from '../../../common/AppSnackbar';
import { useDebounce } from '../../../common/Debounce.hooks';
import { useFindUsers } from '../../../user/User.hooks';
import { User } from '../../../user/User.interface';
import { tasks } from '../training-task-sample';
import { UserContext } from '../../../user/User.context';

const defaultLimit = 5;

export interface TrainingCreateSubTaskDialogProps {
  trainingId: number;
  open: boolean;
  onCancel?: () => void;
  onSubTaskCreated?: (task: any) => void;
}

const TrainingCreateSubTaskDialog: React.FC<
  TrainingCreateSubTaskDialogProps
> = ({ trainingId, open, onCancel, onSubTaskCreated }) => {
  const task = tasks.find(t => t.id === trainingId);
  const { user } = useContext(UserContext);
  const onSubTaskCreatedRef = useRef(onSubTaskCreated);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const debouncedSearch = useDebounce(search || '', 300);
  const previousSearch = useRef('');
  const { userList, isLoading, error, get } = useFindUsers();
  useEffect(() => {
    // handle searching
    if (debouncedSearch !== previousSearch.current) {
      previousSearch.current = debouncedSearch;
      setPage(0);
    }
    const params = {
      filter: debouncedSearch,
      page,
      limit
    } as any;
    get(params);
  }, [debouncedSearch, get, limit, page]);

  const isSelected = useCallback(
    (user: User) => {
      // check if a user is selected
      return selectedUsers.some(u => u.id === user.id);
    },
    [selectedUsers]
  );
  const filteredUsers = useMemo(() => {
    // remove any selected users since we display selected users above the
    // user list
    if (!userList) return [];
    return userList.data.filter(user => !isSelected(user));
  }, [isSelected, userList]);

  const renderRows = (users: User[]) => {
    return users.map(user => (
      <TableRow
        key={user.id}
        hover
        role='checkbox'
        color='primary'
        selected={isSelected(user)}
        onClick={() => {
          if (isSelected(user)) {
            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
          } else {
            setSelectedUsers([...selectedUsers, user]);
          }
        }}
      >
        <TableCell padding='checkbox'>
          <Checkbox color='primary' checked={isSelected(user)} />
        </TableCell>
        <TableCell>{user.firstName}</TableCell>
        <TableCell>{user.lastName}</TableCell>
        <TableCell>
          {user.groups?.map(group => group.name).join(', ')}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Assign Reviewers</DialogTitle>
      <DialogContent>
        <DialogContentText>Select users to review.</DialogContentText>
        <Box mt={1} mb={2}>
          <form autoComplete='off' onSubmit={event => event.preventDefault()}>
            <TextField
              variant='outlined'
              autoFocus
              fullWidth
              autoComplete='off'
              type='text'
              name='name'
              label='Employee Name'
              onChange={event => setSearch(event.target.value)}
            />
          </form>
          {selectedUsers.length !== 3 && (
            <Box mt={1}>
              <Alert color='warning'>
                Assign 3 reviewers (Tech Lead, QA, and Product Lead)
              </Alert>
            </Box>
          )}
        </Box>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Team</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {renderRows(selectedUsers)}
                {renderRows(filteredUsers)}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={userList?.total || 0}
            rowsPerPage={limit}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
          <AppSnackbar severity='error' open={Boolean(error)}>
            There was an error loading the list of users.
          </AppSnackbar>
        </Card>
        {/* {assignError && (
          <Alert severity='error'>
            Error occurred. Please check your internet connection and try again.
          </Alert>
        )} */}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant='contained'
          color='primary'
          disabled={selectedUsers.length !== 3}
          // disabled={isLoading || isAssigning}
          // endIcon={
          //   isAssigning && <CircularProgress color='primary' size={20} />
          // }
          onClick={() => {
            const userIds = selectedUsers.map(u => u.id);
            console.log('selectedUsers', selectedUsers);
            // assignUsers({ userIds });
            if (task) {
              // task.children = selectedUsers.map((selectedUser, index) => {
              //   return {
              //     id: 2000 + index,
              //     name: `Review ${task.name}`,
              //     type: TaskType.Reviewer,
              //     status: 'In Review',
              //     created: new Date(),
              //     modified: new Date(),
              //     assigned: selectedUser,
              //     createdBy: user as User,
              //     modifiedBy: user as User
              //   };
              // });
            }
            onSubTaskCreated?.(userIds);
          }}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingCreateSubTaskDialog;
